import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { adminApi } from "../services/api";
import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";
import { UserAccessCard } from "../features/account-access/UserAccessCard";

interface OrderStats {
	_id: string;
	count: number;
	totalAmount: number;
}

interface UserDetails {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	displayName?: string;
	role: string;
	isExpert: boolean;
	isClient: boolean;
	avatarUrl?: string;
	emailVerified: boolean;
	authProvider: string;
	isPasswordless?: boolean;
	stripeAccountId?: string;
	stripeOnboardingCompleted?: boolean;
	stripeCustomerId?: string;
	averageRating?: number;
	totalRatings?: number;
	subscriptionTier?: string;
	subscriptionStatus?: string;
	subscriptionCurrentPeriodEnd?: string;
	expectedResponseHours?: number;
	createdAt: string;
	updatedAt: string;
	stats: {
		expertChats: number;
		clientChats: number;
		products: number;
		expertOrders: OrderStats[];
		clientOrders: OrderStats[];
	};
}

interface UserChat {
	id: string;
	expertId: string;
	clientEmail: string;
	clientName?: string;
	status: string;
	createdAt: string;
	isExpertRole: boolean;
	product: {
		name: string;
		price: number;
	};
	order?: {
		amount: number;
		status: string;
		mediationRequestedAt?: string;
		/** Date de la décision du support : la médiation est alors close. */
		supportDecisionAt?: string;
	};
}

export function UserDetailPage() {
	const { t } = useI18n();
	const { formatCents, formatUnits } = useMoneyFormatter();
	const { userId } = useParams<{ userId: string }>();
	const [user, setUser] = useState<UserDetails | null>(null);
	const [chats, setChats] = useState<UserChat[]>([]);
	const [loading, setLoading] = useState(true);
	const [chatsLoading, setChatsLoading] = useState(false);
	const [error, setError] = useState("");
	const [chatRole, setChatRole] = useState<"all" | "expert" | "client">("all");
	const [impersonating, setImpersonating] = useState(false);
	// Confirmation en deux temps pour la bascule expert (pas de window.confirm)
	const [confirmingExpertToggle, setConfirmingExpertToggle] = useState(false);
	const [togglingExpert, setTogglingExpert] = useState(false);
	const [expertToggleError, setExpertToggleError] = useState("");

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			setLoading(true);
			try {
				const result = await adminApi.get<UserDetails>(`/admin/users/${userId}`);
				setUser(result);
			} catch (err) {
				setError(err instanceof Error ? err.message : t("adminUserDetail.loadFailed"));
			} finally {
				setLoading(false);
			}
		};

		fetchUser();
	}, [userId]);

	useEffect(() => {
		const fetchChats = async () => {
			if (!userId) return;
			setChatsLoading(true);
			try {
				const params = new URLSearchParams();
				if (chatRole !== "all") params.append("role", chatRole);

				const result = await adminApi.get<{ chats: UserChat[] }>(
					`/admin/users/${userId}/chats?${params}`,
				);
				setChats(result.chats);
			} catch {
				// Ignore chat loading errors
			} finally {
				setChatsLoading(false);
			}
		};

		fetchChats();
	}, [userId, chatRole]);

	const formatDate = (date?: string) => {
		if (!date) return t('adminUsersPage.notAvailable');
		return new Date(date).toLocaleString();
	};

	const getOrderStatsTotal = (stats: OrderStats[]): { count: number; amount: number } => {
		return stats.reduce(
			(acc, stat) => ({
				count: acc.count + stat.count,
				amount: acc.amount + stat.totalAmount,
			}),
			{ count: 0, amount: 0 },
		);
	};

	const handleImpersonate = async () => {
		if (!userId) return;
		setImpersonating(true);
		try {
			const result = await adminApi.post<{ url: string }>(`/admin/users/${userId}/impersonate`, {});
			// Open in a new tab
			window.open(result.url, "_blank");
		} catch (err) {
			alert(err instanceof Error ? err.message : t("impersonation.failed"));
		} finally {
			setImpersonating(false);
		}
	};

	const handleToggleExpert = async () => {
		if (!userId || !user) return;
		setTogglingExpert(true);
		setExpertToggleError("");
		try {
			const result = await adminApi.patch<{ success: boolean; isExpert: boolean }>(
				`/admin/users/${userId}/expert`,
				{ isExpert: !user.isExpert },
			);
			setUser({ ...user, isExpert: result.isExpert });
			setConfirmingExpertToggle(false);
		} catch (err) {
			setExpertToggleError(err instanceof Error ? err.message : t("adminUserDetail.expertStatusFailed"));
		} finally {
			setTogglingExpert(false);
		}
	};

	if (loading) {
		return <div className="loading">{t('adminUserDetail.loading')}</div>;
	}

	if (error || !user) {
		return (
			<div>
				<div className="detail-header">
					<Link to="/users" className="back-link">
						{t('adminGuestDetail.backToUsers')}
					</Link>
				</div>
				<div className="error-message">{error || t('adminUserDetail.notFound')}</div>
			</div>
		);
	}

	const expertOrderStats = getOrderStatsTotal(user.stats.expertOrders);
	const clientOrderStats = getOrderStatsTotal(user.stats.clientOrders);

	return (
		<div>
			<div className="detail-header">
				<Link to="/users" className="back-link">
					{t('adminGuestDetail.backToUsers')}
				</Link>
				<h1>
					{user.displayName || `${user.firstName} ${user.lastName}`}
				</h1>
				<div style={{ display: "flex", gap: "0.25rem", marginLeft: "1rem" }}>
					{user.isExpert && <span className="status-badge accepted">{t('adminMediations.expert')}</span>}
					{user.isClient && <span className="status-badge pending">{t('adminMediations.client')}</span>}
				</div>
				<div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginLeft: "auto" }}>
					{confirmingExpertToggle ? (
						<>
							<span style={{ fontSize: "0.85rem", color: "var(--text-muted, #666)" }}>
								{user.isExpert ? t('adminUserDetail.revokeExpertConfirm') : t('adminUserDetail.grantExpertConfirm')}
							</span>
							<button
								className="btn btn-outline"
								onClick={() => setConfirmingExpertToggle(false)}
								disabled={togglingExpert}
							>
								{t('common.cancel')}
							</button>
							<button
								className="btn btn-primary"
								onClick={handleToggleExpert}
								disabled={togglingExpert}
							>
								{togglingExpert ? t('profile.saving') : t('adminUserDetail.confirm')}
							</button>
						</>
					) : (
						<button
							className="btn btn-outline"
							onClick={() => setConfirmingExpertToggle(true)}
						>
							{user.isExpert ? t('adminUserDetail.revokeExpert') : t('adminUserDetail.grantExpert')}
						</button>
					)}
					<button
						className="btn btn-outline"
						onClick={handleImpersonate}
						disabled={impersonating}
					>
						{impersonating ? t('adminUserDetail.opening') : t('adminUserDetail.impersonateUser')}
					</button>
				</div>
			</div>
			{expertToggleError && <div className="error-message">{expertToggleError}</div>}

			{/* User Info Cards */}
			<div className="detail-grid">
				{/* Basic Info */}
				<div className="info-card">
					<h3>{t('adminGuestDetail.basicInfo')}</h3>
					<div className="info-row">
						<span className="info-label">{t('auth.login.email')}</span>
						<span className="info-value">{user.email}</span>
					</div>
					<div className="info-row">
						<span className="info-label">{t('adminUserDetail.name')}</span>
						<span className="info-value">
							{user.firstName} {user.lastName}
						</span>
					</div>
					{user.displayName && (
						<div className="info-row">
							<span className="info-label">{t('profile.displayName')}</span>
							<span className="info-value">{user.displayName}</span>
						</div>
					)}
					<div className="info-row">
						<span className="info-label">{t('adminGuestDetail.authProvider')}</span>
						<span className="info-value" style={{ textTransform: "capitalize" }}>
							{user.authProvider}
						</span>
					</div>
					<div className="info-row">
						<span className="info-label">{t('adminGuestDetail.emailVerified')}</span>
						<span className="info-value">{user.emailVerified ? t('adminUserDetail.yes') : t('adminUserDetail.no')}</span>
					</div>
					<div className="info-row">
						<span className="info-label">{t('adminGuestDetail.memberSince')}</span>
						<span className="info-value">{formatDate(user.createdAt)}</span>
					</div>
				</div>

				<UserAccessCard user={user} />

				{/* Expert Stats */}
				{user.isExpert && (
					<div className="info-card">
						<h3>{t('adminUserDetail.expertStats')}</h3>
						<div className="info-row">
							<span className="info-label">{t('adminUsersPage.rating')}</span>
							<span className="info-value">
								{user.averageRating
									? t('adminUserDetail.ratingValue', { rating: user.averageRating.toFixed(1), count: user.totalRatings ?? 0 })
									: t('adminUsersPage.noRatings')}
							</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t('productsPage.title')}</span>
							<span className="info-value">{user.stats.products}</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t('adminUserDetail.chatsExpert')}</span>
							<span className="info-value">{user.stats.expertChats}</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t('adminUserDetail.ordersExpert')}</span>
							<span className="info-value">
								{expertOrderStats.count} ({formatCents(expertOrderStats.amount, "eur")})
							</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t('profile.responseTime')}</span>
							<span className="info-value">
								{t('time.hours', { count: user.expectedResponseHours || 24 })}
							</span>
						</div>
					</div>
				)}

				{/* Client Stats */}
				{user.isClient && (
					<div className="info-card">
						<h3>{t('adminUserDetail.clientStats')}</h3>
						<div className="info-row">
							<span className="info-label">{t('adminUserDetail.chatsClient')}</span>
							<span className="info-value">{user.stats.clientChats}</span>
						</div>
						<div className="info-row">
							<span className="info-label">{t('adminUserDetail.ordersClient')}</span>
							<span className="info-value">
								{clientOrderStats.count} ({formatCents(clientOrderStats.amount, "eur")})
							</span>
						</div>
						{user.stripeCustomerId && (
							<div className="info-row">
								<span className="info-label">{t('adminUserDetail.stripeCustomer')}</span>
								<span className="info-value" style={{ fontSize: "0.75rem" }}>
									{user.stripeCustomerId}
								</span>
							</div>
						)}
					</div>
				)}

				{/* Stripe & Subscription */}
				{user.isExpert && (
					<div className="info-card">
						<h3>{t('adminUserDetail.stripeAndSubscription')}</h3>
						<div className="info-row">
							<span className="info-label">{t('adminUserDetail.stripeConnected')}</span>
							<span className="info-value">
								{user.stripeOnboardingCompleted ? t('adminUserDetail.yes') : t('adminUserDetail.no')}
							</span>
						</div>
						{user.stripeAccountId && (
							<div className="info-row">
								<span className="info-label">{t('adminUserDetail.stripeAccount')}</span>
								<span className="info-value" style={{ fontSize: "0.75rem" }}>
									{user.stripeAccountId}
								</span>
							</div>
						)}
						<div className="info-row">
							<span className="info-label">{t('nav.subscription')}</span>
							<span className="info-value">
								{user.subscriptionTier ? (
									<span
										className={`status-badge ${
											user.subscriptionStatus === "active" ? "completed" : "pending"
										}`}
									>
										{user.subscriptionTier} ({user.subscriptionStatus})
									</span>
								) : (
									t('adminGuestDetail.none')
								)}
							</span>
						</div>
						{user.subscriptionCurrentPeriodEnd && (
							<div className="info-row">
								<span className="info-label">{t('adminUserDetail.periodEnds')}</span>
								<span className="info-value">
									{formatDate(user.subscriptionCurrentPeriodEnd)}
								</span>
							</div>
						)}
					</div>
				)}
			</div>

			{/* User's Chats */}
			<div className="card">
				<div className="card-header">
					<h2>{t('adminGuestDetail.chatsCount', { count: chats.length })}</h2>
					<select
						className="filter-select"
						value={chatRole}
						onChange={(e) => setChatRole(e.target.value as typeof chatRole)}
						style={{ marginLeft: "auto" }}
					>
						<option value="all">{t('adminUsersPage.allRoles')}</option>
						<option value="expert">{t('adminUserDetail.asExpert')}</option>
						<option value="client">{t('adminUserDetail.asClient')}</option>
					</select>
				</div>
				{chatsLoading ? (
					<div className="loading">{t('chatsPage.loading')}</div>
				) : chats.length === 0 ? (
					<div className="empty-state">
						<h3>{t('adminGuestDetail.noChats')}</h3>
						<p>{t('adminUserDetail.noChatsBody')}</p>
					</div>
				) : (
					<div className="table-container">
						<table>
							<thead>
								<tr>
									<th>{t('adminUsersPage.role')}</th>
									<th>{t('adminChatsPage.product')}</th>
									<th>{t('adminChatsPage.status')}</th>
									<th>{t('adminChatsPage.order')}</th>
									<th>{t('adminChatsPage.created')}</th>
									<th>{t('adminChatsPage.actions')}</th>
								</tr>
							</thead>
							<tbody>
								{chats.map((chat) => (
									<tr key={chat.id}>
										<td>
											<span
												className={`status-badge ${
													chat.isExpertRole ? "accepted" : "pending"
												}`}
											>
												{chat.isExpertRole ? t('adminMediations.expert') : t('adminMediations.client')}
											</span>
										</td>
										<td>
											<div>{chat.product.name}</div>
											<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
												{formatUnits(chat.product.price, "eur")}
											</div>
										</td>
										<td>
											<span className={`status-badge ${chat.status}`}>
												{chat.status}
											</span>
										</td>
										<td>
											{chat.order ? (
												<div>
													<span className={`status-badge ${chat.order.status}`}>
														{chat.order.status}
													</span>
													{chat.order.mediationRequestedAt && (
														<span
															className={`status-badge ${chat.order.supportDecisionAt ? "completed" : "mediation"}`}
															style={{ marginLeft: "0.25rem" }}
														>
															{chat.order.supportDecisionAt
																? t('adminChatsPage.mediationResolved')
																: t('adminChatsPage.mediation')}
														</span>
													)}
												</div>
											) : (
												<span style={{ color: "var(--color-gray-400)" }}>{t('adminChatsPage.noOrder')}</span>
											)}
										</td>
										<td>{formatDate(chat.createdAt)}</td>
										<td>
											<Link to={`/chats/${chat.id}`} className="table-link">
												{t('chatsPage.view')}
											</Link>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}
